import { z } from 'zod';
import {
  CountryCodeSchema,
  CurrencySchema,
  IdSchema,
  LocaleSchema,
  PaginationQuerySchema,
  paginatedSchema,
} from './common';
import { FiscalProfileSchema } from './fiscal';

export const TENANT_STATUSES = [
  /**
   * Signed up, country not chosen yet, no database provisioned.
   *
   * Self-service sign-up starts here: an address and a password are collected,
   * the country is chosen next, and the tenant database is created only once
   * the country pack is known. A tenant in this state can do nothing except
   * pick a country, which is why it holds no data.
   */
  'ONBOARDING',
  'PROVISIONING',
  'ACTIVE',
  'SUSPENDED',
  'ARCHIVED',
  'FAILED',
] as const;
export const TenantStatusSchema = z.enum(TENANT_STATUSES);
export type TenantStatus = z.infer<typeof TenantStatusSchema>;

/**
 * The plans a workspace can be on, and the only names the API accepts.
 *
 * The billing catalogue in the control plane is seeded with exactly these
 * codes; `plan-catalogue.spec.ts` in the API is what keeps the two lists
 * honest, because a plan the contract publishes and the catalogue has never
 * heard of is a subscription that cannot be created.
 */
export const TENANT_PLANS = ['free', 'start', 'business', 'enterprise'] as const;
export const TenantPlanSchema = z.enum(TENANT_PLANS);
export type TenantPlan = z.infer<typeof TenantPlanSchema>;

/** Public tenant profile returned by the API (never exposes the DSN). */
export const TenantSchema = z.object({
  id: IdSchema,
  slug: z.string(),
  name: z.string(),
  status: TenantStatusSchema,
  plan: TenantPlanSchema,
  /**
   * ISO 3166-1 alpha-2, or `null` while the company is still onboarding.
   *
   * It selects the country pack, so it decides tax rates, identity validation
   * and document types. Locked once the tenant has written its first business
   * row; an operator can force a change, and that is audited.
   */
  country: CountryCodeSchema.nullable(),
  /** Version of the country pack applied, or `null` if never applied. */
  countryVersion: z.string().nullable(),
  locale: LocaleSchema,
  currency: CurrencySchema,
  timezone: z.string(),
  /** Physical database name, e.g. `tenant_acme`. */
  databaseName: z.string(),
  contactEmail: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Tenant = z.infer<typeof TenantSchema>;

export const TenantListSchema = paginatedSchema(TenantSchema);

export const CreateTenantSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, { message: 'validation.slug' })
    .transform((value) => value.toLowerCase()),
  name: z.string().trim().min(1).max(200),
  adminEmail: z.email().transform((value) => value.toLowerCase()),
  adminPassword: z.string().min(8).max(200),
  adminFullName: z.string().trim().min(1).max(200),
  /**
   * ISO 3166-1 alpha-2, the country the company operates in.
   *
   * When present the tenant is provisioned straight away and the country pack
   * supplies `locale`, `currency` and `timezone`. When absent the tenant is
   * created in `ONBOARDING` with no database at all — which is what
   * self-service sign-up does, because the country is chosen on the next
   * screen and the pack is what decides what gets seeded.
   */
  country: CountryCodeSchema.optional(),
  /**
   * Interface language, currency and zone.
   *
   * Left unset they come from the country pack, which is the right answer for
   * almost every tenant: a Chilean company wants Spanish, CLP and
   * America/Santiago. They stay settable because a pack is a default, not a
   * cage — an Argentine company that invoices in dollars can say so.
   */
  locale: LocaleSchema.optional(),
  currency: CurrencySchema.optional(),
  timezone: z.string().trim().min(1).max(64).optional(),
  plan: TenantPlanSchema.default('free'),
  contactEmail: z.email().optional(),
  /** Seed demo catalog + stock so a fresh tenant is not an empty shell. */
  seedDemoData: z.boolean().default(false),
});
export type CreateTenant = z.infer<typeof CreateTenantSchema>;

export const UpdateTenantSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  plan: TenantPlanSchema.optional(),
  status: TenantStatusSchema.optional(),
  locale: LocaleSchema.optional(),
  currency: CurrencySchema.optional(),
  timezone: z.string().trim().min(1).max(64).optional(),
  contactEmail: z.email().nullable().optional(),
});
export type UpdateTenant = z.infer<typeof UpdateTenantSchema>;

/**
 * Confirmation for a destructive platform operation.
 *
 * Deprovisioning drops a client's database irreversibly, so the caller has to
 * echo the tenant's slug back. An id is opaque and easy to pass by mistake
 * (a stale variable, a copy-paste from the wrong row); typing the slug means
 * the operator had to know which tenant they were aiming at.
 */
export const DeleteTenantSchema = z.object({
  confirmSlug: z.string().trim().min(1).max(40),
});
export type DeleteTenant = z.infer<typeof DeleteTenantSchema>;

export const TenantQuerySchema = PaginationQuerySchema.extend({
  status: TenantStatusSchema.optional(),
  plan: TenantPlanSchema.optional(),
});
export type TenantQuery = z.infer<typeof TenantQuerySchema>;
export type TenantQueryInput = z.input<typeof TenantQuerySchema>;

/** Result of the async provisioning pipeline. */
export const ProvisioningResultSchema = z.object({
  tenant: TenantSchema,
  steps: z.array(
    z.object({
      name: z.string(),
      status: z.enum(['ok', 'skipped', 'failed']),
      durationMs: z.number().nonnegative(),
      detail: z.string().optional(),
    }),
  ),
});
export type ProvisioningResult = z.infer<typeof ProvisioningResultSchema>;

/** Branding/tenant settings a signed-in user is allowed to read. */
export const TenantSettingsSchema = z.object({
  name: z.string(),
  slug: z.string(),
  locale: LocaleSchema,
  currency: CurrencySchema,
  timezone: z.string(),
  /** The company's own Argentine fiscal identity. */
  fiscal: FiscalProfileSchema,
  /** Configurable document number prefixes. */
  numbering: z.object({
    RECEIPT: z.string(),
    ISSUE: z.string(),
    TRANSFER: z.string(),
    WRITE_OFF: z.string(),
    STOCKTAKE: z.string(),
  }),
});
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;

/**
 * A country as the operator console sees it, which is not how a customer does.
 *
 * The public list omits anything unverified, because a customer who picks a
 * draft country gets invoices their tax authority may reject. An operator has
 * the opposite need: they have to be able to see the draft, and to see *why*
 * it is a draft, without reading the pack's source. That is what `unverified`
 * is for — it names the exact fiscal values nobody has confirmed yet.
 */
export const AdminCountryOptionSchema = z.object({
  code: CountryCodeSchema,
  name: z.string(),
  currency: CurrencySchema,
  timezone: z.string(),
  defaultLocale: z.string(),
  /** `stable` once the fiscal values have been checked against the authority. */
  readiness: z.enum(['stable', 'draft']),
  /** Pack revision, recorded on a tenant when it is applied. */
  version: z.string(),
  /** ISO-8601 date this revision takes effect. */
  validFrom: z.string(),
  /**
   * What a draft pack has not confirmed yet, in plain words. Empty for a
   * stable pack — the two cannot drift apart.
   */
  unverified: z.array(z.string()),
});
export type AdminCountryOption = z.infer<typeof AdminCountryOptionSchema>;
